import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getQueryFn, apiRequest } from '../../lib/queryClient';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select } from '../ui/select';
import { useAuth } from '../../hooks/use-auth';
import { useToast } from '../../hooks/use-toast';
import type { Country } from '../../types';

interface Props {
  companyId: number | null | undefined;
  onSaved: () => void;
}

interface FuelPriceRow {
  company_id?: number | null;
  country: string;
  fuel_type: string;
  currency: string;
  price_per_liter: number;
  effective_date: string;
}

export function Scope1Scenario2Form({ companyId, onSaved }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: companyCountries } = useQuery<Country[]>({
    queryKey: companyId ? [`/api/countries/company/${companyId}`] : [null],
    queryFn: getQueryFn(),
    enabled: !!companyId,
  });

  const [country, setCountry] = useState('');
  const [fuelType, setFuelType] = useState<'Diesel' | 'Petrol'>('Diesel');
  const [currency, setCurrency] = useState('AED');
  const [totalAmount, setTotalAmount] = useState<string>('');
  const [overridePrice, setOverridePrice] = useState<string>('');
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear());
  const [notes, setNotes] = useState('');
  const [priceDraft, setPriceDraft] = useState('');

  const { data: members } = useQuery<any[]>({
    queryKey: companyId ? [`/api/companies/${companyId}/members`] : [null],
    queryFn: getQueryFn(),
    enabled: !!companyId && !!user && !user.is_system_user,
  });

  const canEditPrice = !!user && (
    user.is_system_user ||
    user.role === 'admin' ||
    (members || []).some((member) => member.user_id === user.id && member.role === 'COMPANY_ADMIN' && member.is_active)
  );

  const priceQueryKey = ['/api/ghg/scope1/fuel-prices', { country, company_id: companyId }] as const;

  const { data: prices } = useQuery<FuelPriceRow[]>({
    queryKey: priceQueryKey,
    queryFn: getQueryFn(),
    enabled: !!country && !!companyId,
  });

  const latestPrice = useMemo(() => {
    const rows = (prices || []).filter((p) => p.fuel_type === fuelType && p.country === country);
    return rows.find((p) => p.company_id === companyId) || rows[0] || null;
  }, [prices, fuelType, country, companyId]);

  useEffect(() => {
    if (latestPrice) {
      setPriceDraft(String(latestPrice.price_per_liter));
      setCurrency(latestPrice.currency);
    } else {
      setPriceDraft('');
    }
  }, [latestPrice]);

  const savePrice = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error('Select a company first');
      const res = await apiRequest('POST', '/api/ghg/scope1/fuel-prices', {
        company_id: companyId,
        country,
        fuel_type: fuelType,
        currency,
        price_per_liter: Number(priceDraft),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: priceQueryKey });
      toast({ title: 'Price saved', description: 'The latest fuel price is updated for this company.' });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not save price', description: error.message, variant: 'destructive' });
    },
  });

  const computed = useMemo(() => {
    const price = overridePrice ? Number(overridePrice) : latestPrice?.price_per_liter || 0;
    const liters = price > 0 ? (Number(totalAmount || 0) / price) : 0;
    return { price, liters };
  }, [overridePrice, latestPrice, totalAmount]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error('Select a company first');
      const payload: any = {
        company_id: companyId,
        fuel_type: fuelType,
        country,
        country_code: country || undefined,
        currency,
        total_amount_paid: Number(totalAmount || 0),
        reporting_year: reportingYear,
        notes: notes || undefined,
      };
      if (overridePrice) payload.avg_price_per_liter = Number(overridePrice);
      const res = await apiRequest('POST', '/api/ghg/scope1/scenario2', payload);
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      return res.json();
    },
    onSuccess: () => onSaved(),
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Country"
          value={country}
          options={[
            { value: '', label: 'Select country…' },
            ...(companyCountries || []).map((c: Country) => ({ value: c.code, label: c.name }))
          ]}
          onChange={(e: any) => setCountry(e?.target ? e.target.value : e)}
        />
        <div>
          <label className="block text-sm text-gray-600 mb-1">Fuel type</label>
          <select className="w-full border rounded px-3 py-2" value={fuelType} onChange={(e) => setFuelType(e.target.value as any)}>
            <option>Diesel</option>
            <option>Petrol</option>
          </select>
        </div>
        <Input label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
        <Input label="Total amount paid" type="number" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} />
        <Input label="Override price per liter (optional)" type="number" value={overridePrice} onChange={(e) => setOverridePrice(e.target.value)} />
        <div className="self-end text-sm text-gray-700 space-y-2">
          <div className="text-lg font-bold text-green-800 bg-green-50 px-4 py-3 rounded-lg border-2 border-green-200">
            Latest price: {latestPrice ? `${latestPrice.currency} ${latestPrice.price_per_liter}` : '—'}
          </div>
          {canEditPrice && (
            <div className="flex items-end gap-2">
              <Input
                label="Price per liter"
                type="number"
                min="0"
                step="any"
                value={priceDraft}
                onChange={(e) => setPriceDraft(e.target.value)}
                disabled={!country}
              />
              <Button
                type="button"
                onClick={() => savePrice.mutate()}
                disabled={savePrice.isPending || !country || !(Number(priceDraft) > 0)}
              >
                {savePrice.isPending ? 'Saving...' : 'Update price'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Reporting Year"
          type="number"
          min="2000"
          max="2100"
          value={reportingYear}
          onChange={(e) => setReportingYear(parseInt(e.target.value) || new Date().getFullYear())}
          required
        />
        <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
      </div>

      <div className="flex items-center gap-2">
        <div className="ml-auto">
          <div className="text-lg font-bold text-green-800 bg-green-50 px-4 py-3 rounded-lg border-2 border-green-200">
            Estimated fuel: {computed.liters.toFixed(2)} L @ {computed.price || 0}
          </div>
        </div>
      </div>

      <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !companyId}>
        {mutation.isPending ? 'Saving...' : 'Save Estimation'}
      </Button>
    </div>
  );
}
